import { useMutation } from "@tanstack/react-query";
import { Form, FormikProvider, useFormik } from "formik";
import { useContext } from "react";
import { useTranslation } from "react-i18next";
import z from "zod";
import { toFormikValidate, toFormikValidationSchema } from "zod-formik-adapter";

import { apiV2 } from "../../../../../../../libs/api/src/lib/apiV2";
import { ModalContext, ModalWrapper } from "../../../../providers";
import { AuthenticationContext } from "../../../../providers/authentication/AuthenticationContext";
import { ToastContext } from "../../../../providers/ToastProvider/context";
import { Button, FormInput, Spinner } from "../../../atoms";
import { XIcon } from "../../../atoms/icons/XIcon";

type FormValues = {
  voucherCode: string;
};

const VoucherCodeSchema = z.object({
  voucherCode: z
    .string()
    .min(1)
    .regex(/^\s*[A-Za-z0-9]{4}-[A-Za-z0-9]{4}-[A-Za-z0-9]{4}\s*$/, {
      message: "validation.invalidVoucherCodeFormat",
    }),
});

const EnterVoucherCodeModal = ({ memberId }: { memberId: string }) => {
  const { t } = useTranslation();
  const { popModal } = useContext(ModalContext);
  const { toast } = useContext(ToastContext);
  const { userType } = useContext(AuthenticationContext);

  const { mutateAsync: redeemVoucher, isPending } = useMutation(
    apiV2[userType].wallet.mutations.redeem(memberId),
  );

  const formik = useFormik({
    initialValues: { voucherCode: "" },
    validationSchema: toFormikValidationSchema(VoucherCodeSchema),
    validate: toFormikValidate(VoucherCodeSchema),
    onSubmit: async (values: FormValues) => {
      await redeemVoucher({ voucherCode: values.voucherCode?.trim() ?? "" });
      popModal();
      toast({
        message: t(
          "components.userWallets.topUp.voucherRedeem.successMessage.title",
        ),
        description: t(
          "components.userWallets.topUp.voucherRedeem.successMessage.description",
        ),
        intent: "success",
      });
    },
  });

  return (
    <FormikProvider value={formik}>
      <Form
        className="flex h-full grow flex-col items-center gap-6"
        onSubmit={formik.handleSubmit}
      >
        <ModalWrapper
          className="w-full max-w-[25rem] !p-0"
          overlayClassName="p-0 max-lg:justify-center"
        >
          {isPending ? (
            <div className="flex size-full flex-col items-center justify-center gap-6 py-8">
              <Spinner
                circleClassName="stroke-secondary-700"
                className="size-10"
              />
              <div className="dark:text-gray-0 text-gray-950">
                {t("components.userWallets.topUp.loadingTitle")}
              </div>
            </div>
          ) : (
            <div className="flex flex-col justify-between">
              <Button
                intent="transparent"
                className="self-end"
                onClick={popModal}
              >
                <XIcon pathClassName="stroke-gray-600" />
              </Button>

              <div className="flex h-40 flex-col gap-4 px-4">
                <div className="dark:text-gray-0 mx-16 self-center text-center text-lg font-semibold text-gray-950">
                  {t("components.userWallets.topUp.voucherRedeem.title")}
                </div>

                <FormInput
                  name="voucherCode"
                  placeholder={t(
                    "components.userWallets.topUp.items.enterTheVoucherCode",
                  )}
                  isRequired
                />
              </div>
              <div className="flex w-full justify-between gap-4 border-t px-4 py-5">
                <Button onClick={popModal} className="w-44">
                  {t("portal.common.cancel")}
                </Button>
                <Button
                  disabled={!formik.dirty}
                  intent="secondary"
                  onClick={() => {
                    formik.handleSubmit();
                  }}
                  className="w-44"
                >
                  {t("portal.common.confirm")}
                </Button>
              </div>
            </div>
          )}
        </ModalWrapper>
      </Form>
    </FormikProvider>
  );
};

export default EnterVoucherCodeModal;
